// frontend/src/components/EmployeeAttendanceRequest.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
  Grid,
  Paper,
  Divider,
  IconButton,
  FormHelperText
} from '@mui/material';
import {
  Close,
  AccessTime,
  EventNote,
  Person,
  Email,
  Manager,
  Send,
  History
} from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function EmployeeAttendanceRequest({ open, onClose, onSuccess, selectedDate = null }) {
  const [formData, setFormData] = useState({
    date: selectedDate || null,
    requested_in_time: '',
    requested_out_time: '',
    reason: ''
  });
  const [originalAttendance, setOriginalAttendance] = useState(null);
  const [employeeInfo, setEmployeeInfo] = useState(null);
  const [managerInfo, setManagerInfo] = useState(null);
  const [existingRequest, setExistingRequest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingDate, setCheckingDate] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
const [employeeId, setEmployeeId] = useState(null);

  const getEmployeeIdFromToken = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
      
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      // Try to get from user API
      try {
        const response = await axios.get(`${API_URL}/api/user/current?company_id=${company_id}`, {
        withCredentials: true
      });
        
        if (response.data && response.data.employee_id) {
          setEmployeeId(response.data.employee_id);
          return response.data.employee_id;
        }
      } catch (err) {
      }
      
      // Fallback to payload
      if (payload.employee_id) {
        setEmployeeId(payload.employee_id);
        return payload.employee_id;
      }
      
      throw new Error('Employee ID not found');
    } catch (err) {
      //console.error("Error fetching current user:", err);
      setError('Failed to fetch user information');
      return null;
    }
  };
  const getCompanyIdFromToken = () => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.company_id;
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    if (open) {
      fetchEmployeeInfo();
      if (formData.date) {
        fetchOriginalAttendance();
        checkExistingRequest();
      }
    }
  }, [open, formData.date]);

  const fetchEmployeeInfo = async () => {
    try {
      const employee_id = await getEmployeeIdFromToken();
      const token = localStorage.getItem('token');
      const company_id = getCompanyIdFromToken();
      
      if (!employee_id || !company_id) return;

      // Fetch employee details
      const empResponse = await axios.get(`${API_URL}/api/attendance-requests/employee/${employee_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
           
          }
        }
      );
      setEmployeeInfo(empResponse.data);

      // Fetch manager details from role
      const roleResponse = await axios.get(`${API_URL}/api/attendance-requests/roles/${employee_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
           
          }
        }
      );
      if (roleResponse.data && roleResponse.data.manager_id) {
        const managerResponse = await axios.get(`${API_URL}/api/attendance-requests/employee/${roleResponse.data.manager_id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
             
            }
          }
        );
        setManagerInfo(managerResponse.data);
      }
    } catch (error) {
      //console.error('Error fetching employee info:', error);
    }
  };

  const fetchOriginalAttendance = async () => {
    if (!formData.date) return;
    
    try {
      const token = localStorage.getItem('token');
      const employee_id = await getEmployeeIdFromToken();
      const company_id = getCompanyIdFromToken();
      
      const response = await axios.get(
        `${API_URL}/api/attendance-requests/attendance/${employee_id}&startDate=${formData.date}&endDate=${formData.date}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
         
          }
        }
      );
      
      if (response.data && response.data.length > 0) {
        setOriginalAttendance(response.data[0]);
        // Pre-fill with existing times for editing
        setFormData(prev => ({
          ...prev,
          requested_in_time: response.data[0].in_time || '',
          requested_out_time: response.data[0].out_time || ''
        }));
      } else {
        setOriginalAttendance(null);
      }
    } catch (error) {
      //console.error('Error fetching attendance:', error);
    }
  };

  const checkExistingRequest = async () => {
    if (!formData.date) return;
    
    setCheckingDate(true);
    try {
      const token = localStorage.getItem('token');
      const employee_id = await getEmployeeIdFromToken();
      const company_id = getCompanyIdFromToken();
      
      const response = await axios.get(
        `${API_URL}/api/attendance-requests/check/${employee_id}/${formData.date}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
           
          }
        }
      );
      
      if (response.data.exists) {
        setExistingRequest(response.data.data);
        setError('A pending request already exists for this date. Please wait for manager approval.');
      } else {
        setExistingRequest(null);
        setError('');
      }
    } catch (error) {
      //console.error('Error checking request:', error);
    } finally {
      setCheckingDate(false);
    }
  };

  const handleDateChange = (newDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (newDate && newDate >= today) {
      setError('Cannot request correction for today or future dates');
      return;
    }
    
    setFormData({ ...formData, date: newDate });
    setError('');
  };

  const handleSubmit = async () => {
    if (!formData.date || !formData.requested_in_time || !formData.requested_out_time || !formData.reason) {
      setError('Please fill all required fields');
      return;
    }

    setLoading(true);
    try {
      const employee_id = await getEmployeeIdFromToken();
      const company_id = getCompanyIdFromToken();
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/attendance-requests/submit`,
        {
          employee_id,
          date: formData.date.toISOString().split('T')[0],
          requested_in_time: formData.requested_in_time,
          requested_out_time: formData.requested_out_time,
          reason: formData.reason,
        },{
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      
      if (response.data.success) {
        setSnackbar({ open: true, message: 'Request submitted successfully!', severity: 'success' });
        setTimeout(() => {
          onSuccess && onSuccess();
          onClose();
        }, 1500);
      }
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Failed to submit request', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" fontWeight="bold">
              Request Attendance Correction
            </Typography>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ mt: 2 }}>
              {/* Employee Info Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8f9fa' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Person color="primary" />
                      <Typography variant="body2" color="textSecondary">Employee Name</Typography>
                    </Box>
                    <Typography variant="body1" fontWeight="bold">
                      {employeeInfo?.firstName} {employeeInfo?.lastName}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8f9fa' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Email color="primary" />
                      <Typography variant="body2" color="textSecondary">Employee Email</Typography>
                    </Box>
                    <Typography variant="body1" fontWeight="bold">
                      {employeeInfo?.companyemail}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fff3e0' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Person color="warning" />
                      <Typography variant="body2" color="textSecondary">Reporting Manager</Typography>
                    </Box>
                    <Typography variant="body1" fontWeight="bold">
                      {managerInfo ? `${managerInfo.firstName} ${managerInfo.lastName}` : 'Loading...'}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Date Picker */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  <EventNote sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Select Date
                </Typography>
                <DatePicker
                  label="Correction Date"
                  value={formData.date}
                  onChange={handleDateChange}
                  disableFuture
                  sx={{ width: '100%' }}
                />
                {checkingDate && <CircularProgress size={20} sx={{ mt: 1 }} />}
                {error && <FormHelperText error>{error}</FormHelperText>}
              </Box>

              {/* Original Attendance Info */}
              {originalAttendance && formData.date && !checkingDate && (
                <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Current Attendance Record
                  </Typography>
                  <Box display="flex" gap={2} flexWrap="wrap">
                    <Chip 
                      icon={<AccessTime />} 
                      label={`In: ${originalAttendance.in_time || 'Not recorded'}`}
                      size="small"
                    />
                    <Chip 
                      icon={<AccessTime />} 
                      label={`Out: ${originalAttendance.out_time || 'Not recorded'}`}
                      size="small"
                    />
                  </Box>
                </Box>
              )}

              {/* Request Form */}
              {formData.date && !existingRequest && !checkingDate && (
                <>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Requested Check-in Time"
                        type="time"
                        value={formData.requested_in_time}
                        onChange={(e) => setFormData({ ...formData, requested_in_time: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Requested Check-out Time"
                        type="time"
                        value={formData.requested_out_time}
                        onChange={(e) => setFormData({ ...formData, requested_out_time: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                        required
                      />
                    </Grid>
                  </Grid>

                  <TextField
                    fullWidth
                    label="Reason for Correction"
                    multiline
                    rows={4}
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    placeholder="Please provide a detailed reason for requesting this attendance correction..."
                    required
                    sx={{ mb: 2 }}
                  />
                </>
              )}

              {existingRequest && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">Pending Request Exists</Typography>
                  <Typography variant="body2">
                    You already have a pending request for this date. 
                    Please wait for your manager to review it.
                  </Typography>
                </Alert>
              )}
            </Box>
          </LocalizationProvider>
        </DialogContent>
        
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !formData.date || existingRequest || !formData.reason || !formData.requested_in_time || !formData.requested_out_time}
            startIcon={loading ? <CircularProgress size={20} /> : <Send />}
          >
            {loading ? 'Submitting...' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default EmployeeAttendanceRequest;